import { getPackagesStatus } from '../api/API';
import moment from "moment";
import { coinValidityDate } from '../utils/coinValidity';
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Modal } from "react-bootstrap";
import { RootStoreContext } from "../stores/RootStore";
import { observer } from "mobx-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faChevronLeft, faChevronDown, faChevronUp, faSignOutAlt, faHome, faTrophy, faFire, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { formatRelativeDate, stripHtml } from '../util';
import BurgerMenu from './BurgerMenu';
import ReferralPopup from './ReferralPopup';
import StreakDashboardCard from './registration/progress/StreakDashboardCard';
import WeeklyHeatmap from './registration/progress/WeeklyHeatmap';
import MilestoneBadges from './registration/progress/MilestoneBadges';
import MilestoneCelebration from './registration/progress/MilestoneCelebration';
import CheckinSuggestionDialog from './CheckinSuggestionDialog';
import StreakRewardsList from './registration/progress/StreakRewardsList';
import UserProfileModal from './UserProfileModal';
import NoActivePackageDialog from './NoActivePackageDialog';
import './TerminalHeader.css';
import { clearPendingEventPath } from '../stores/EventDeepLinkSession';
import { PopupPriority } from '../stores/PopupStore';
import { getExpiringPackageDays, isTrainingPackageActive } from '../util/trainingPackage';
import { StepName } from '../config/Wizard';
import {
  markShown,
  PACKAGE_WARNING_DISMISSAL_KEY,
  wasShownWithin
} from '../util/displayCooldown';

const NO_ACTIVE_PACKAGE_POPUP_ID = 'no-active-package-dialog';
const PACKAGE_WARNING_DISMISSAL_MINUTES = 60;

const TerminalHeader = observer(() => {
  const store = useContext(RootStoreContext);
  const navigate = useNavigate();
  const [isReferralPopupOpen, setIsReferralPopupOpen] = useState(false);
  const [isStreakExpanded, setIsStreakExpanded] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPackageOpen, setIsPackageOpen] = useState(false);
  const [profileNotification, setProfileNotification] = useState<string | null>(null);
  const [isActivatingPackage, setIsActivatingPackage] = useState(false);
  const [packageActivationError, setPackageActivationError] = useState<string | null>(null);
  const [packageActivationSuccess, setPackageActivationSuccess] = useState<string | null>(null);
  const [activatedEarlyBirdPackageId, setActivatedEarlyBirdPackageId] = useState<number | null>(null);
  const [dismissedNoPackageUserId, setDismissedNoPackageUserId] = useState<number | null>(null);
  const [dismissedPackageWarningUserId, setDismissedPackageWarningUserId] = useState<number | null>(null);

  useEffect(() => {
    // Fetch studio configs if not already loaded
    if (!store.studioConfigStore.studioConfigs) {
      store.studioConfigStore.fetchStudioConfigs();
    }
  }, [store.studioConfigStore]);

  const {
    registrationStore: {
      user,
      restart,
      isCheckinStep,
      isHistoryStep,
      isProgressStep,
      isLeaderboardStep,
      toBookingHistory,
      toMyProgress,
      toSellingItems,
      clearSelectedItems,
      studentProgress,
      leaderboard,
      toLeaderboard,
      activateEarlyBirdPackage
    },
    authStore,
    studioConfigStore,
    popupStore
  } = store;

  const [privateBalance, setPrivateBalance] = useState<{ userId: number; coins: number } | null>(null);
  const balanceUserId = user?.id;
  const privateCoinBalance = privateBalance && privateBalance.userId === balanceUserId ? privateBalance.coins : 0;
  const privateClassesAllowed = authStore.isUserFlow && !!user?.id && studioConfigStore.isPrivateClassesAllowed?.(user.id);
  useEffect(() => {
    let active = true;
    let requestId = 0;
    const refresh = () => {
      if (!balanceUserId || !privateClassesAllowed) { setPrivateBalance(null); return; }
      const currentRequest = ++requestId;
      getPackagesStatus(balanceUserId).then(status => {
        if (active && currentRequest === requestId) setPrivateBalance({ userId: balanceUserId, coins: status.private_coin_balance ?? 0 });
      }).catch(() => { if (active && currentRequest === requestId) setPrivateBalance(null); });
    };
    refresh();
    window.addEventListener('focus', refresh);
    window.addEventListener('private-coins-changed', refresh);
    return () => { active = false; window.removeEventListener('focus', refresh); window.removeEventListener('private-coins-changed', refresh); };
  }, [balanceUserId, privateClassesAllowed]);

  const rawPendingEarlyBirdPackage = user?.pendingEarlyBirdPackage;
  const pendingEarlyBirdPackage = rawPendingEarlyBirdPackage?.id === activatedEarlyBirdPackageId
    ? undefined
    : rawPendingEarlyBirdPackage;
  const canManagePackagesMembership = authStore.isUserFlow;
  const hasActiveMembership = user?.hasActiveMembership === true;
  const noActivePackageUserId = user?.id && !hasActiveMembership && !isTrainingPackageActive(user.latestTrainingPackage)
    ? user.id
    : null;
  const shouldShowNoActivePackage = noActivePackageUserId !== null
    && dismissedNoPackageUserId !== noActivePackageUserId;
  const hasLowCoins = !!user?.id
    && !hasActiveMembership
    && !!studioConfigStore.studioConfigs
    && isTrainingPackageActive(user.latestTrainingPackage)
    && user.latestTrainingPackage.availableCoins < studioConfigStore.packagesLowThresholdCoins;
  const expiringInDays = !hasActiveMembership && studioConfigStore.studioConfigs
    ? getExpiringPackageDays(
        user?.latestTrainingPackage,
        studioConfigStore.packagesExpiringThresholdDays
      )
    : null;
  const hasPackageWarning = hasLowCoins || expiringInDays !== null;
  const hasRecentPackageWarningDismissal = !!user?.id && wasShownWithin(
    PACKAGE_WARNING_DISMISSAL_KEY,
    user.id,
    PACKAGE_WARNING_DISMISSAL_MINUTES
  );
  const shouldShowPackageWarning = hasPackageWarning
    && dismissedPackageWarningUserId !== user?.id
    && !hasRecentPackageWarningDismissal;
  const shouldShowPackageNotice = shouldShowNoActivePackage || shouldShowPackageWarning;

  useEffect(() => {
    if (!shouldShowPackageNotice) {
      return;
    }

    popupStore.register(NO_ACTIVE_PACKAGE_POPUP_ID, PopupPriority.NO_ACTIVE_PACKAGE);
    return () => popupStore.unregister(NO_ACTIVE_PACKAGE_POPUP_ID);
  }, [popupStore, shouldShowPackageNotice]);

  const dismissNoActivePackageDialog = (persistPackageWarningDismissal: boolean) => {
    if (noActivePackageUserId !== null) {
      setDismissedNoPackageUserId(noActivePackageUserId);
    } else if (hasPackageWarning && user?.id) {
      setDismissedPackageWarningUserId(user.id);
      if (persistPackageWarningDismissal) {
        markShown(PACKAGE_WARNING_DISMISSAL_KEY, user.id);
      }
    }
  };

  const closeNoActivePackageDialog = () => dismissNoActivePackageDialog(true);

  const choosePackageOrMembership = () => {
    dismissNoActivePackageDialog(false);
    clearSelectedItems();

    if (canManagePackagesMembership) {
      navigate('/packages-membership');
      return;
    }

    if (studioConfigStore.studioHomepageStore) {
      window.location.href = studioConfigStore.studioHomepageStore;
    }
  };

  const handleLogout = () => {
    clearPendingEventPath();
    if (authStore.isUserFlow) {
      authStore.logout();
      restart();
    } else {
      restart();
    }
    navigate('/');
  }

  const handleHomeClick = () => {
    clearSelectedItems();
    navigate('/');
    toSellingItems();
  };

  const handleHistoryButtonClick = () => {
    clearSelectedItems();
    navigate('/');
    toBookingHistory();
  };

  const handleProgressButtonClick = () => {
    clearSelectedItems();
    navigate('/');
    toMyProgress();
  };

  const handlePackagesButtonClick = () => {
    clearSelectedItems();
    navigate('/packages-membership');
  };

  const handleWhatsNewClick = () => {
    clearSelectedItems();
    store.eventPhotoStore.selectCampaign(null);
    navigate('/');
    store.wizardStore.setStep(StepName.WHATS_NEW);
  };

  const handlePackageChipClick = () => {
    if (canManagePackagesMembership) {
      handlePackagesButtonClick();
    } else {
      setIsPackageOpen(true);
    }
  };

  const handleReferFriend = () => {
    setIsReferralPopupOpen(true);
  };

  const handleCloseReferralPopup = () => {
    setIsReferralPopupOpen(false);
  };

  const handleActivateEarlyBird = async () => {
    if (!pendingEarlyBirdPackage || !pendingEarlyBirdPackage.canActivateEarlyBird) {
      return;
    }

    const packageName = pendingEarlyBirdPackage.packageName;
    const packageId = pendingEarlyBirdPackage.id;
    setIsActivatingPackage(true);
    setPackageActivationError(null);
    setPackageActivationSuccess(null);
    try {
      await activateEarlyBirdPackage(packageId);
      setActivatedEarlyBirdPackageId(packageId);
      setPackageActivationSuccess(`${packageName} activated. Your coins are ready to use.`);
    } catch (error: any) {
      const errorMessage = error?.message || error?.error || 'Package activation failed. Please try again or contact admin.';
      if (errorMessage === 'PACKAGE_ALREADY_ACTIVE') {
        setActivatedEarlyBirdPackageId(packageId);
        setPackageActivationSuccess(`${packageName} is already active. Your coins are ready to use.`);
        return;
      }
      setPackageActivationError(errorMessage);
    } finally {
      setIsActivatingPackage(false);
    }
  };

  const handleClosePackageModal = () => {
    setIsPackageOpen(false);
    setPackageActivationError(null);
    setPackageActivationSuccess(null);
  };

  const handleProfileSaved = (message: string) => {
    setProfileNotification(message);
  };

  useEffect(() => {
    if (!profileNotification) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setProfileNotification(null);
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [profileNotification]);

  const getPaymentStatus = () => {
    switch (user.latestTrainingPackage.packageStatus) {
      case "status_paid":
      case "status_closed":
        return "PAID";
      default:
        return "NOT PAID";
    }
  };

  const hasActiveNonSinglePackage = () => {
    if (!user.latestTrainingPackage || user.latestTrainingPackage.isSinglePackage || !user.latestTrainingPackage.isActive) {
      return false;
    }
    const isActive = user.latestTrainingPackage.isActive;
    const isNotExpired = moment(user.latestTrainingPackage.endDate).isAfter(moment());
    return isActive && isNotExpired;
  };

  const formatEarlyBirdWindow = () => {
    if (!pendingEarlyBirdPackage?.activateFromDate || !pendingEarlyBirdPackage?.activateUntilDate) {
      return '';
    }

    const from = moment(pendingEarlyBirdPackage.activateFromDate).format('D MMM YYYY');
    const until = moment(pendingEarlyBirdPackage.activateUntilDate).format('D MMM YYYY');
    return `${from} - ${until}`;
  };

  const getEarlyBirdChipStatus = () => {
    if (pendingEarlyBirdPackage && !isPendingEarlyBirdPaid()) {
      return 'Payment pending';
    }

    if (!pendingEarlyBirdPackage?.activateFromDate || !pendingEarlyBirdPackage?.activateUntilDate) {
      return 'Pending activation';
    }

    const today = moment().startOf('day');
    const from = moment(pendingEarlyBirdPackage.activateFromDate).startOf('day');
    const until = moment(pendingEarlyBirdPackage.activateUntilDate).startOf('day');

    if (today.isBefore(from)) {
      return `Opens ${from.format('D MMM')}`;
    }

    if (today.isAfter(until)) {
      return 'Window ended';
    }

    return `Activate by ${until.format('D MMM')}`;
  };

  const isPendingEarlyBirdPaid = () => {
    return pendingEarlyBirdPackage?.packageStatus === 'status_paid'
      || pendingEarlyBirdPackage?.packageStatus === 'status_closed';
  };

  const getEarlyBirdActivationHint = () => {
    if (!pendingEarlyBirdPackage) {
      return '';
    }

    if (!isPendingEarlyBirdPaid()) {
      return 'Payment must be completed before activation.';
    }

    if (!pendingEarlyBirdPackage.activateFromDate || !pendingEarlyBirdPackage.activateUntilDate) {
      return 'Activation dates are missing. Please contact admin.';
    }

    const today = moment().startOf('day');
    const from = moment(pendingEarlyBirdPackage.activateFromDate).startOf('day');
    const until = moment(pendingEarlyBirdPackage.activateUntilDate).startOf('day');

    if (today.isBefore(from)) {
      return `Activation is available from ${from.format('D MMM YYYY')}.`;
    }

    if (today.isAfter(until)) {
      return `Activation window ended on ${until.format('D MMM YYYY')}.`;
    }

    return `Activation is available until ${until.format('D MMM YYYY')}.`;
  };

  const renderHistoryButton = () => {
    if (isHistoryStep || isProgressStep) {
      return (
        <button
          className="terminal-action-button"
          onClick={handleHomeClick}
        >
          <div className="button-content">
            <FontAwesomeIcon icon={faChevronLeft} className="button-icon" />
            <span className="button-text">Back</span>
          </div>
        </button>
      );
    }

    return (
      <>
        <button
          className="terminal-action-button"
          onClick={toBookingHistory}
        >
          <div className="button-content">
            <FontAwesomeIcon icon={faHistory} className="button-icon" />
            <span className="button-text">History</span>
          </div>
        </button>
        <button
          className="terminal-action-button"
          onClick={handleProgressButtonClick}
        >
          <div className="button-content">
            <FontAwesomeIcon icon={faTrophy} className="button-icon" />
            <span className="button-text">Progress</span>
          </div>
        </button>
      </>
    );
  };

  return (
    <div className="terminal-header">
      {user && (
        <div className="terminal-header-content">
          <div className={`terminal-button-container ${authStore.isUserFlow ? 'terminal-left-actions' : ''}`}>
            {authStore.isUserFlow ? (
              <BurgerMenu
                user={user}
                onHistoryClick={handleHistoryButtonClick}
                onProgressClick={handleProgressButtonClick}
                onHomeClick={handleHomeClick}
                onLogoutClick={handleLogout}
                onProfileClick={() => setIsProfileOpen(true)}
                onPackagesMembershipClick={handlePackagesButtonClick}
                onWhatsNewClick={store.eventPhotoStore?.adCampaigns.length > 0 ? handleWhatsNewClick : undefined}
                isCheckinStep={isCheckinStep}
                isHistoryStep={isHistoryStep}
                isProgressStep={isProgressStep}
              />
            ) : renderHistoryButton()}
          </div>
          <div className="terminal-user-info">
            {(authStore.isAdminFlow) && (
              <h2 className="user-greeting">Welcome {user.firstName}!</h2>
            )}
            <div className="header-package-row">
              {privateClassesAllowed && !hasActiveNonSinglePackage() && <button type="button" className="coin-chip" aria-label="Private Coin balance" onClick={handlePackagesButtonClick}>
                <span className="coin-chip-coins">{privateCoinBalance} PȻ</span>
              </button>}
              {hasActiveNonSinglePackage() && (
                <div
                  className={`coin-chip ${getPaymentStatus() === "NOT PAID" ? 'coin-chip-unpaid' : ''}`}
                  onClick={handlePackageChipClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePackageChipClick(); } }}
                  aria-label={canManagePackagesMembership ? 'Packages & Membership' : 'Show package details'}
                  title={canManagePackagesMembership ? 'Packages & Membership' : 'Package details'}
                >
                  <span className="coin-chip-currency">Ȼ</span>
                  <span className="coin-chip-coins">{user.latestTrainingPackage.availableCoins}</span>
                  <span className="coin-chip-separator">·</span>
                  <span className="coin-chip-expiry">
                    {coinValidityDate(user.latestTrainingPackage.endDate, "D MMM")}
                  </span>
                  {privateClassesAllowed && <>
                    <span className="coin-chip-separator">·</span>
                    <span className="coin-chip-coins" aria-label="Private Coin balance">{privateCoinBalance} PȻ</span>
                  </>}
                  {pendingEarlyBirdPackage && (
                    <>
                      <span className="coin-chip-separator">·</span>
                      <span className="coin-chip-early-bird-pill">Early bird</span>
                    </>
                  )}
                  {getPaymentStatus() === "NOT PAID" && (
                    <span className="coin-chip-unpaid-dot" aria-label="Not paid" />
                  )}
                </div>
              )}
              {pendingEarlyBirdPackage && !hasActiveNonSinglePackage() ? (
                <button
                  className="coin-chip coin-chip-cta coin-chip-button coin-chip-early-bird"
                  onClick={handlePackageChipClick}
                  aria-label={canManagePackagesMembership ? 'Packages & Membership' : 'Show early bird package details'}
                  title={canManagePackagesMembership ? 'Packages & Membership' : 'Package details'}
                >
                  <span className="coin-chip-label">Early bird: {pendingEarlyBirdPackage.packageName}</span>
                  <span className="coin-chip-separator">·</span>
                  <span className="coin-chip-expiry">{getEarlyBirdChipStatus()}</span>
                </button>
              ) : !hasActiveNonSinglePackage() && canManagePackagesMembership ? (
                <button
                  className="coin-chip coin-chip-cta coin-chip-button"
                  onClick={handlePackagesButtonClick}
                  aria-label="Packages & Membership"
                >
                  Packages &amp; Membership
                </button>
              ) : !hasActiveNonSinglePackage() ? (
                <a
                  className="coin-chip coin-chip-cta"
                  href={studioConfigStore.studioHomepageStore}
                >
                  + Choose package
                </a>
              ) : null}
            </div>
            <div className="header-secondary-row">
              {!isLeaderboardStep && leaderboard && leaderboard.currentUserRank != null && (
                <div
                  className="leaderboard-badge-header"
                  onClick={() => {
                    navigate('/');
                    toLeaderboard();
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate('/');
                      toLeaderboard();
                    }
                  }}
                  aria-label="Open leaderboard"
                >
                  <FontAwesomeIcon icon={faTrophy} className="leaderboard-badge-icon" />
                  <span className="leaderboard-badge-rank">#{leaderboard.currentUserRank}</span>
                </div>
              )}
              {studentProgress?.attendanceStats && (
                <div
                  className={`streak-badge-header ${studentProgress.attendanceStats.currentStreak > 0 ? 'streak-active' : ''} ${isStreakExpanded ? 'expanded' : ''}`}
                  onClick={() => setIsStreakExpanded(!isStreakExpanded)}
                >
                  <FontAwesomeIcon icon={faFire} className="streak-badge-icon" />
                  <span className="streak-badge-count">
                    {studentProgress.attendanceStats.currentStreak > 0
                      ? `${studentProgress.attendanceStats.currentStreak}w Streak`
                      : 'Start Streak'}
                  </span>
                  <FontAwesomeIcon icon={isStreakExpanded ? faChevronUp : faChevronDown} className="streak-badge-chevron" />
                </div>
              )}
            </div>
          </div>
          <div className={`terminal-button-container terminal-right-actions ${authStore.isUserFlow ? 'terminal-user-actions' : ''}`}>
            {authStore.isUserFlow ? (
              <button
                className="terminal-action-button refer-friend"
                onClick={handleReferFriend}
              >
                <div className="button-content">
                  <FontAwesomeIcon icon={faUserPlus} className="button-icon" />
                  <span className="button-text">Invite a friend</span>
                </div>
              </button>
            ) : (
              <button
                className="terminal-action-button logout"
                onClick={handleLogout}
              >
                <div className="button-content">
                  <FontAwesomeIcon icon={faSignOutAlt} className="button-icon" />
                  <span className="button-text">Logout</span>
                </div>
              </button>
            )}
          </div>
        </div>
      )}
      {profileNotification && (
        <div className="profile-save-notification" role="status" aria-live="polite">
          {profileNotification}
        </div>
      )}
      
      {/* Backdrop for closing streak panel */}
      <div
        className={`streak-expand-backdrop ${isStreakExpanded && studentProgress?.attendanceStats ? 'open' : ''}`}
        onClick={() => setIsStreakExpanded(false)}
      />

      {/* Expandable Streak Panel */}
      <div className={`streak-expand-panel ${isStreakExpanded && studentProgress?.attendanceStats ? 'open' : ''}`}>
        {studentProgress?.attendanceStats && (
          <div className="streak-expand-content">
            <StreakDashboardCard attendanceStats={studentProgress.attendanceStats} />
            <h4 className="streak-section-title">My Dance Calendar <span className="streak-section-subtitle">(1 field = 1 week)</span></h4>
            {studentProgress.attendanceStats.lastAttendedDate && (
              <div className="last-danced">Last class: {formatRelativeDate(studentProgress.attendanceStats.lastAttendedDate)}</div>
            )}
            {studentProgress.attendanceStats.streakAtRisk && studentProgress.attendanceStats.currentStreak > 0 && (
              <div className="streak-risk-warning">
                ⚠️ Your {studentProgress.attendanceStats.currentStreak} Week Streak is at risk! Dance this week to keep it alive.
              </div>
            )}
            <WeeklyHeatmap attendanceStats={studentProgress.attendanceStats} />
            <h4 className="streak-section-title">Streak Milestones</h4>
            {(() => {
              const achieved = studentProgress.attendanceStats.milestones?.filter(m => m.achieved) || [];
              if (achieved.length === 0) return null;
              const highest = achieved.reduce((max, m) => (m.weekThreshold > max.weekThreshold ? m : max), achieved[0]);
              return (
                <p className="streak-section-current-milestone">
                  Current achieved milestone: <span className="streak-section-current-milestone-title">{stripHtml(highest.message)}</span>
                </p>
              );
            })()}
            <MilestoneBadges
              milestones={studentProgress.attendanceStats.milestones}
              currentStreak={studentProgress.attendanceStats.currentStreak}
              nextMilestoneWeeks={studentProgress.attendanceStats.nextMilestoneWeeks}
            />
            <h4 className="streak-section-title">Streak Rewards</h4>
            <StreakRewardsList
              rewards={studentProgress.attendanceStats.streakRewards || []}
              compact
            />
            {studentProgress.attendanceStats.streakInfoMessage && (
              <div
                className="streak-info-message"
                dangerouslySetInnerHTML={{ __html: studentProgress.attendanceStats.streakInfoMessage }}
              />
            )}
          </div>
        )}
      </div>

      {/* Milestone Celebration Overlay (student mode only) */}
      {authStore.isUserFlow && studentProgress?.attendanceStats?.streakRewards && user?.id && (
        <MilestoneCelebration
          rewards={studentProgress.attendanceStats.streakRewards}
          userId={user.id}
          onViewRewards={() => setIsStreakExpanded(true)}
        />
      )}

      {/* Early check-in suggestion dialog (self-gates via suggestionStore) */}
      {user?.id && <CheckinSuggestionDialog />}

      <NoActivePackageDialog
        show={shouldShowPackageNotice && popupStore.isActive(NO_ACTIVE_PACKAGE_POPUP_ID)}
        lowCoins={hasLowCoins}
        expiringInDays={expiringInDays}
        onClose={closeNoActivePackageDialog}
        onChoosePackage={choosePackageOrMembership}
      />

      <ReferralPopup
        isOpen={isReferralPopupOpen}
        onClose={handleCloseReferralPopup}
        refToken={`${studioConfigStore.appBaseUrl}/ref/${user?.referralToken}`}
      />
      {user && (
        <UserProfileModal
          show={isProfileOpen}
          onHide={() => setIsProfileOpen(false)}
          onSaved={handleProfileSaved}
          user={user}
        />
      )}

      {!canManagePackagesMembership && (user?.latestTrainingPackage || pendingEarlyBirdPackage) && (
        <Modal
          show={isPackageOpen}
          onHide={handleClosePackageModal}
          centered
          dialogClassName="package-modal-dialog"
        >
          <Modal.Header closeButton>
            <Modal.Title>Package details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {packageActivationSuccess && (
              <div className="package-activation-success">{packageActivationSuccess}</div>
            )}
            {packageActivationError && !pendingEarlyBirdPackage && (
              <div className="package-activation-error">{packageActivationError}</div>
            )}
            {user.latestTrainingPackage && (
              <div className="package-section">
                <h4 className="package-section-title">
                  {user.latestTrainingPackage.packageName} package
                  {/Free/i.test(user.latestTrainingPackage.packageName) && (
                    <span className="package-free-pill">FREE</span>
                  )}
                </h4>
                <div className="package-detail-row">
                  <span className="package-detail-label">Coins available</span>
                  <span className="package-detail-value">Ȼ {user.latestTrainingPackage.availableCoins}</span>
                </div>
                <div className="package-detail-row">
                  <span className="package-detail-label">Expires</span>
                  <span className="package-detail-value">
                    {coinValidityDate(user.latestTrainingPackage.endDate)}
                  </span>
                </div>
                <div className="package-detail-row">
                  <span className="package-detail-label">Payment</span>
                  <span className="package-detail-value">
                    {getPaymentStatus() === "PAID" ? (
                      <Badge bg="success" pill>PAID</Badge>
                    ) : (
                      <Badge bg="danger" pill>NOT PAID</Badge>
                    )}
                  </span>
                </div>
              </div>
            )}
            {pendingEarlyBirdPackage && (
              <div className="package-section package-section-pending">
                <h4 className="package-section-title">{pendingEarlyBirdPackage.packageName} package</h4>
                <div>
                  <span className="package-early-bird-pill">EARLY BIRD</span>
                  {!pendingEarlyBirdPackage.isActive && (
                    <span className="package-inactive-pill">INACTIVE</span>
                  )}
                </div>
                <div className="package-detail-row">
                  <span className="package-detail-label">Purchased coins</span>
                  <span className="package-detail-value">Ȼ {pendingEarlyBirdPackage.availableCoins}</span>
                </div>
                <div className="package-detail-row">
                  <span className="package-detail-label">Activation window</span>
                  <span className="package-detail-value">{formatEarlyBirdWindow()}</span>
                </div>
                {user.latestTrainingPackage && (
                  <>
                    <div className="package-detail-row">
                      <span className="package-detail-label">Current package</span>
                      <span className="package-detail-value">{user.latestTrainingPackage.packageName}</span>
                    </div>
                    <div className="package-activation-summary">
                      Activating transfers the remaining Ȼ {user.latestTrainingPackage.availableCoins} from {user.latestTrainingPackage.packageName},
                      deactivates that package, and starts {pendingEarlyBirdPackage.packageName} from now.
                    </div>
                  </>
                )}
                {packageActivationError && (
                  <div className="package-activation-error">{packageActivationError}</div>
                )}
                <Button
                  className="package-activate-button"
                  disabled={!pendingEarlyBirdPackage.canActivateEarlyBird || isActivatingPackage}
                  onClick={handleActivateEarlyBird}
                >
                  {isActivatingPackage ? 'Activating...' : `Activate ${pendingEarlyBirdPackage.packageName}`}
                </Button>
                {!pendingEarlyBirdPackage.canActivateEarlyBird && (
                  <div className="package-activation-hint">{getEarlyBirdActivationHint()}</div>
                )}
              </div>
            )}
          </Modal.Body>
        </Modal>
      )}

    </div>
  );
});

export default TerminalHeader;
